import React, { useState, useReducer, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/styles";
import { AppContext, DispatchContext } from "../AppContext";
import * as actions from "../actions/actions";
import { strings } from '../locate';
import { config, prices, resolveNotificationMessage } from '../helpers/constants';
import Service from "../services/service";
import { ReactComponent as SettingsIcon } from '../assets/icons/help.svg';
import FavouritesIconSrc from '../assets/icons/favourites.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as UserIcon } from '../assets/icons/user.svg';
import { ReactComponent as WalletIcon } from '../assets/icons/wallet.svg';
import { ReactComponent as ApprovalIcon } from '../assets/icons/approval.svg';
import { ReactComponent as LockedIcon } from '../assets/icons/locked.svg';
import { ReactComponent as SepaIcon } from '../assets/icons/sepa.svg';
import { ReactComponent as SofortIcon } from '../assets/icons/sofort.svg';
import { ReactComponent as SearchSettingsIcon } from '../assets/icons/settings-search.svg';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { Link, Slider, Switch, FormGroup, Select, FormControlLabel, FormHelperText, Paper, Grid, Box, Typography, InputLabel, FormControl, Button, Tab, Tabs, AppBar, Dialog, List, DialogActions, DialogContent, DialogTitle, ListItem, TextField, MenuItem } from "@material-ui/core";
import Autocomplete from '@material-ui/lab/Autocomplete';
import IconLabel from './IconLabel';
import PriceCard from './PriceCard';
import LicenseQuotePanel from './LicenseQuotePanel';
import CheckoutLoginPanel from './CheckoutLoginPanel';
import CheckoutInvoiceAddressPanel from './CheckoutInvoiceAddressPanel';
import CheckoutSelectPaymentPanel from './CheckoutSelectPaymentPanel';
import { FreeBreakfast } from "@material-ui/icons";
import DividerLabel from './DividerLabel';
import { initialState } from './../reducer/InitialState';
import { SimilarityCurve } from './CompareSource';


function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}



export default function SettingsDialog() {
  const useStyles = makeStyles((theme) => ({
    dialogContent: {
      borderTop: "1px solid #eeeeee",
      borderBottom: "1px solid #eeeeee",
      height: "520px",
      width: "850px",
      padding: 0
    },
    tabContent: {
    },
    paper: {
      height: "280px",
      width: 100,
    },
    tab: {
      textTransform: "none !important",
    },
    marginPaper: {
      marginLeft: "50px",
      marginRight: "50px",
      minHeight: "100px",
    },
    textField: {
      width: "100%",
      marginBottom: theme.spacing(2)
    },
    switch: {
      width: "100%",
      marginBottom: theme.spacing(1)
    },
    slider: {
      width: "100%",
      marginBottom: theme.spacing(1),
      marginLeft: 4
    },
  }));

  const state = useContext(AppContext);
  const s = state.strings;
  const dispatch = useContext(DispatchContext);
  const [settings, setSettings] = useState({});
  const [changed, setChanged] = useState(false);
  const classes = useStyles();


  const [selectedTab, setSelectedTab] = useState(0);
  // update local settings from state when dialog becomes visible
  useEffect(() => {
    if (state.ui.settingsDialogVisible) {
      const s = state.user.settings;
      setSettings(s.minSimilarity === 0 ? { ...s, legacyMatchMode: true } : s);
    }
  }, [state.ui.settingsDialogVisible]);

  const handleChange = (newValue, key) => {
    if ((key == "reportFormat") && (newValue == "cst")) {
      setSettings({ ...settings, [key]: newValue, reportRenderComments: false, reportIncludeGuide: false });
    } else if ((key == 'reportRetentionSwitch')) {
      setSettings({ ...settings, [key]: newValue, reportRetentionDays: (newValue) ? 28 : 0 });
    } else if ((key == 'libtextRetentionSwitch')) {
      setSettings({ ...settings, [key]: newValue, libtextRetentionDays: (newValue) ? 365 : 0 });
    } else if ((key == 'reportPermalinkReadPolicy')) {  // make sure read access is at least at same level as write access
      setSettings({ ...settings, 'reportPermalinkReadPolicy': newValue, 'reportPermalinkWritePolicy': Math.min(newValue, settings['reportPermalinkWritePolicy'])});
    } else if ((key == 'reportPermalinkWritePolicy')) { // make sure read access is at least at same level as write access
      setSettings({ ...settings, 'reportPermalinkWritePolicy': newValue, 'reportPermalinkReadPolicy': Math.max(newValue, settings['reportPermalinkReadPolicy'])});
    } else if (key === 'legacyMatchMode' && !newValue) {
      setSettings({ ...settings, legacyMatchMode: false, minSimilarity: initialState.user.settings.minSimilarity });
    } else {
      setSettings({ ...settings, [key]: newValue });
    }
  }

  useEffect(() => {
    const normalize = (s) => ({ ...s, legacyMatchMode: s.minSimilarity === 0 ? true : !!s.legacyMatchMode });
    setChanged(JSON.stringify(normalize(state.user.settings)) !== JSON.stringify(normalize(settings)));
  }, [settings]);

  const handleClose = () => {
    dispatch(actions.setUI({ settingsDialogVisible: false }));
  }

  const handleReset = (newValue, key) => {
    setSettings(state.user.settings);
  }

  const handleResetDefault = () => {
    const defaultSettings = {
      maxPhraseDistance: initialState.user.settings.maxPhraseDistance,
      minRegionLength: initialState.user.settings.minRegionLength,
      minSourceLength: initialState.user.settings.minSourceLength,
      minSimilarity: initialState.user.settings.minSimilarity,
      legacyMatchMode: false,
    };
    setSettings({ ...settings, ...defaultSettings });
  }

  const handleResetDefaultStudent = () => {
    const defaultSettings = {
      addToLibrary: false,
      useAuthWhitelist: true,
      autoAcceptSources: true,
      searchInLibrary: true,
    };
    setSettings({ ...settings, ...defaultSettings });
  }

  const handleResetDefaultEducation = () => {
    const defaultSettings = {
      addToLibrary: true,
      useAuthWhitelist: true,
      autoAcceptSources: true,
      searchInLibrary: true,
    };
    setSettings({ ...settings, ...defaultSettings });
  }

  const handleSubmit = () => {
    const effectiveSettings = settings.legacyMatchMode
      ? { ...settings, minSimilarity: 0 }
      : settings;
    handleClose();
    dispatch(actions.setUserSettings(effectiveSettings));
    Service.updateUserSettings(effectiveSettings).then(res => {
      if (!res.success) {
        dispatch(actions.openToast({ type: "error", message: res.errors[0] }));
      }
    });
  }

  const handleSelectedTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  }

  const settingsForced = () => {
    return state.user.license && state.user.license.forceUserSettings && !userIsLicenseAdmin();
  }
  const settingsForcedAdmin = () => {
    return state.user.license && state.user.license.forceUserSettings && userIsLicenseAdmin();
  }
  const userIsLicenseAdmin = () => {
    return state.user.license && (state.user.license.adminUserId == state.user.userId);
  }

  return (
    <div>
      <Dialog
        className={classes.root}
        open={state.ui.settingsDialogVisible}
        onClose={handleClose}
        disableBackdropClick
        maxWidth={false}
      >
        <DialogTitle style={{ color: '#0D4A81' }}>{s.settings.title}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <AppBar position="static" color="default">
            <Tabs
              className={classes.tabs}
              value={selectedTab}
              onChange={handleSelectedTabChange}
              indicatorColor="primary"
              textColor="primary"
              centered
            >
              <Tab className={classes.tab} label={s.settings.interfaceTitle} icon={<ApprovalIcon height={60} width={60} />} />
              <Tab className={classes.tab} label={s.settings.engineTitle} icon={<img src={FavouritesIconSrc} height={60} width={60} alt="" />} />
              <Tab className={classes.tab} label={s.settings.sensitivityTitle} icon={<SearchSettingsIcon height={60} width={60} />} />
              {/* <Tab className={classes.tab} label={s.settings.textMonitoring} icon={<LockedIcon height={60} width={60} />} /> */}
              <Tab className={classes.tab} label={s.settings.dataProtectionTitle} icon={<ShieldIcon height={60} width={60} />} />
            </Tabs>
          </AppBar>
          <TabPanel value={selectedTab} index={0}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ flex: "1 1 auto", width: "50%", marginRight: 8 }}>
                <DividerLabel label={s.settings.interfaceSettings} />
                <TextField className={classes.textField} select size="small" label={s.settings.rowsPerPage}
                  value={settings.pagesize}
                  onChange={(e) => handleChange(e.target.value, "pagesize")}
                >
                  <MenuItem key={0} value={10}>{s.settings.rows.format(10)}</MenuItem>
                  <MenuItem key={1} value={25}>{s.settings.rows.format(25)}</MenuItem>
                  <MenuItem key={2} value={50}>{s.settings.rows.format(50)}</MenuItem>
                  <MenuItem key={3} value={100}>{s.settings.rows.format(100)}</MenuItem>
                </TextField>
                <FormControlLabel className={classes.switch}
                  control={<Switch checked={settings.showTooltips} onChange={(e) => handleChange(e.target.checked, "showTooltips")} />}
                  label={s.settings.showTips}
                />

                <DividerLabel label={s.settings.notificationSettings} />
                <TextField className={classes.textField} select size="small" label={s.settings.sendSummaryMail}
                  value={settings.notifyMode}
                  onChange={(e) => handleChange(e.target.value, "notifyMode")}
                >
                  <MenuItem key={'new'} value={'new'}>{s.settings.newMessages}</MenuItem>
                  <MenuItem key={'always'} value={'always'}>{s.settings.sendAlways}</MenuItem>
                  <MenuItem key={'never'} value={'never'}>{s.settings.sendNever}</MenuItem>
                </TextField>

                <FormControlLabel className={classes.switch}
                  control={<Switch checked={settings.reportNotify} onChange={(e) => handleChange(e.target.checked, "reportNotify")} />}
                  label={s.settings.sendResultMail}
                />

              </div>
              <div style={{ flex: "1 1 auto", width: "50%", marginLeft: 8 }}>


                <DividerLabel label={s.settings.defaultReportSettings} />

                <TextField className={classes.textField} select size="small" label={s.settings.reportLanguage}
                  value={settings.reportLang}
                  onChange={(e) => handleChange(e.target.value, "reportLang")}
                >
                  <MenuItem key="lang" value={'de'}>{s.languages.de}</MenuItem>
                  <MenuItem key="lang" value={'en'}>{s.languages.en}</MenuItem>
                </TextField>
                <TextField className={classes.textField} select size="small" label={s.downloadReportDialog.reportFormat}
                  value={settings.reportFormat}
                  onChange={(e) => handleChange(e.target.value, "reportFormat")}
                >
                  <MenuItem key={'docx'} value={'docx'}>{s.downloadReportDialog.formatWord}</MenuItem>
                  <MenuItem key={'pdf'} value={'pdf'}>{s.downloadReportDialog.formatPdf}</MenuItem>
                  <MenuItem key={'cst'} value={'cst'}>{s.downloadReportDialog.formatCustom}</MenuItem>
                </TextField>

                <FormControlLabel className={classes.switch}
                  control={<Switch checked={settings.reportRenderComments} onChange={(e) => handleChange(e.target.checked, "reportRenderComments")} disabled={settings.reportFormat == "cst"} />}
                  label={s.downloadReportDialog.renderTitle}
                />
                <FormControlLabel className={classes.switch}
                  control={<Switch checked={settings.reportIncludeGuide} onChange={(e) => handleChange(e.target.checked, "reportIncludeGuide")} disabled={settings.reportFormat == "cst"} />}
                  label={s.downloadReportDialog.renderGuide}
                />
                <FormControlLabel className={classes.switch}
                  control={<Switch checked={settings.reportTruncate} onChange={(e) => handleChange(e.target.checked, "reportTruncate")} />}
                  label={s.downloadReportDialog.truncateText}
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ flex: "1 1 auto", width: "50%", marginRight: 8 }}>
                  <DividerLabel label={s.settings.newScanDefaults} />
                  <FormControlLabel className={classes.switch}
                    control={<Switch checked={settings.searchInLibrary} onChange={(e) => handleChange(e.target.checked, "searchInLibrary")} />}
                    label={s.sourceLibrarySearch}
                  />
                  <FormControlLabel className={classes.switch}
                    control={<Switch checked={settings.addToLibrary} onChange={(e) => handleChange(e.target.checked, "addToLibrary")} />}
                    label={s.newText.addToLibraryTitle}
                  />
                  <FormControlLabel className={classes.switch}
                    control={<Switch checked={settings.useAuthWhitelist} onChange={(e) => handleChange(e.target.checked, "useAuthWhitelist")} />}
                    label={s.settings.excludeByWhitelist}
                  />
                  <FormControlLabel className={classes.switch}
                    control={<Switch checked={settings.autoAcceptSources} onChange={(e) => handleChange(e.target.checked, "autoAcceptSources")} />}
                    label={s.settings.acceptIndicated}
                  />
                </div>
                <div style={{ flex: "1 1 auto", width: "50%", marginLeft: 8 }}>
                  <DividerLabel label={s.settings.presets} />
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Button style={{ marginTop: 8 }} color="primary" variant="outlined" onClick={handleResetDefaultStudent}>{s.settings.defaultsForStudents}</Button>
                    <Button style={{ marginTop: 8 }} color="primary" variant="outlined" onClick={handleResetDefaultEducation}>{s.settings.defaultsforEducation}</Button>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <DividerLabel label={s.settings.sensitivityHintsTitle} />
                <IconLabel type="info" mt={8} label={s.settings.defaultSettingsHint1} />
              </div>
            </div>
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {settingsForced() && <IconLabel type="warning" mb={8} mt={0} label={s.settings.settingsForced} />}
              {settingsForcedAdmin() && <IconLabel type="warning" mb={8} mt={0} label={s.settings.settingsAdmin} />}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ flex: "1 1 auto", width: "50%", marginRight: 8 }}>
                  <DividerLabel label={s.settings.textComparisonSettings} />
                  {false && /* Deprecated, should only by 125 in order to avoid unexpected behavior */
                    <FormControlLabel className={classes.slider}
                      control={<Slider value={settings.maxPhraseDistance} min={0} max={250} valueLabelDisplay="auto" marks={[{ value: initialState.user.settings.maxPhraseDistance }]} step={5} style={{ width: 100, marginRight: 10 }} onChange={(e, value) => handleChange(value, "maxPhraseDistance")} disabled={settingsForced()} />}
                      label={s.settings.maxPhraseDistance}
                    />}
                  <FormControlLabel className={classes.slider}
                    control={<Slider disabled={settingsForced()} value={settings.minRegionLength} min={10} max={250} valueLabelDisplay="auto" step={10} marks={[{ value: initialState.user.settings.minRegionLength }]} style={{ width: 100, marginRight: 10 }} onChange={(e, value) => handleChange(value, "minRegionLength")} />}
                    label={s.settings.minPhraseLength}
                  />
                  {false && /* Deprecated, replaced by fuzzy search which defaults to 35% min Similarity (dynamic) */
                    <FormControlLabel className={classes.slider}
                      control={<Slider disabled={settingsForced()} value={settings.minSimilarity} min={25} max={100} valueLabelDisplay="auto" step={5} marks={[{ value: initialState.user.settings.minSimilarity }]} style={{ width: 100, marginRight: 10 }} onChange={(e, value) => handleChange(value, "minSimilarity")} />}
                      label={s.settings.minSimilarity}
                    />}
                  <FormControlLabel className={classes.slider}
                    control={<Slider disabled={settingsForced() || !!settings.legacyMatchMode} value={settings.minSimilarity || initialState.user.settings.minSimilarity} min={20} max={100} valueLabelDisplay="auto" step={5} marks={[{ value: initialState.user.settings.minSimilarity }]} style={{ width: 100, marginRight: 10 }} onChange={(e, value) => handleChange(value, "minSimilarity")} />}
                    label={s.settings.minSimilarity}
                  />
                  <FormControlLabel className={classes.switch}
                    control={<Switch disabled={settingsForced()} checked={!!settings.legacyMatchMode} onChange={(e) => handleChange(e.target.checked, "legacyMatchMode")} />}
                    label={s.settings.legacyMatchMode}
                  />
                  <DividerLabel label={s.settings.perSourceSensitivity} />
                  <FormControlLabel className={classes.slider}
                    control={<Slider disabled={settingsForced()} value={settings.minSourceLength} min={10} max={50} valueLabelDisplay="auto" step={5} marks={[{ value: initialState.user.settings.minSourceLength }]} style={{ width: 100, marginRight: 10 }} onChange={(e, value) => handleChange(value, "minSourceLength")} />}
                    label={s.settings.minSourceLength}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                    <Button disabled={settingsForced()} color="primary" variant="outlined" onClick={handleResetDefault}>{s.settings.resetDefault}</Button>
                  </div>
                </div>
                <div style={{ flex: "1 1 auto", width: "50%", marginLeft: 8, paddingTop: 0 }}>
                  <DividerLabel label={s.settings.sensitivityDiagramTitle} />
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
                    {settings.legacyMatchMode
                      ? <IconLabel type="info" mt={8} label={s.settings.legacyNoSimilarityDiagram} />
                      : <SimilarityCurve scale={1.5} minRegionLength={settings.minRegionLength} minSimilarity={settings.minSimilarity || initialState.user.settings.minSimilarity} phraseAxisLabel={s.compareSource?.phraseAxisLabel} />}
                  </div>
                </div>
              </div>
              {false && <div style={{ marginTop: 16 }}>
                <DividerLabel label={s.settings.sensitivityHintsTitle} />
                <IconLabel type="info" mt={8} label={s.settings.sensitivityHint1} />
                <IconLabel type="info" mt={8} label={s.settings.sensitivityHint2} />
              </div>}
            </div>
          </TabPanel>
          {/* Textüberwachung tab panel — hidden, keep for later re-enabling
          <TabPanel value={selectedTab} index={3}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ flex: "1 1 auto", width: "100%", marginRight: 8 }}>
                {settingsForced() && <IconLabel type="warning" mb={8} mt={4} label={s.settings.settingsForced} />}
                {settingsForcedAdmin() && <IconLabel type="warning" mb={8} mt={4} label={s.settings.settingsAdmin} />}

                <FormControlLabel className={classes.switch}
                  control={<Switch disabled={settingsForced()} checked={settings.useTextMonitoring} onChange={(e) => handleChange(e.target.checked, "useTextMonitoring")} />}
                  label={s.settings.enableTextMonitoring}
                />

                <DividerLabel label={s.settings.frequency} />
                <FormControlLabel className={classes.slider}
                  control={<Slider disabled={settingsForced() || !settings.useTextMonitoring} value={settings.scansPerDay} min={0} max={500} step={1} valueLabelDisplay="auto" style={{ width: 600, marginRight: 10 }} onChange={(e, value) => handleChange(value, "scansPerDay")} />}
                  label={(settings.scansPerDay > 0) ? s.settings.scans.format(settings.scansPerDay) : s.settings.noScans}
                />
                <DividerLabel label={s.settings.hitsRating} />
                <FormControlLabel className={classes.slider}
                  control={<Slider disabled={settingsForced() || !settings.useTextMonitoring} value={settings.maxMonitoringHits} min={0} max={50} step={1} valueLabelDisplay="auto" style={{ width: 300, marginRight: 10 }} onChange={(e, value) => handleChange(value, "maxMonitoringHits")} />}
                  label={(settings.maxMonitoringHits > 0) ? s.settings.maxHits.format(settings.maxMonitoringHits) : s.settings.noMaxHits}
                />
                <FormControlLabel className={classes.switch}
                  control={<Switch disabled={settingsForced() || !settings.useTextMonitoring} checked={settings.monitoringIgnoreOwn} onChange={(e) => handleChange(e.target.checked, "monitoringIgnoreOwn")} />}
                  label={s.settings.ignoreOwn}
                />
                <FormControlLabel className={classes.switch}
                  control={<Switch disabled={settingsForced() || !settings.useTextMonitoring} checked={settings.monitoringIgnoreCites} onChange={(e) => handleChange(e.target.checked, "monitoringIgnoreCites")} />}
                  label={s.settings.ignoreCites}
                />
              </div>
            </div>
          </TabPanel>
          */}
          <TabPanel value={selectedTab} index={3}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ flex: "1 1 auto", width: "100%", marginRight: 8 }}>
                {settingsForced() && <IconLabel type="warning" mb={16} mt={8} label={s.settings.settingsForced} />}
                {settingsForcedAdmin() && <IconLabel type="warning" mb={16} mt={8} label={s.settings.settingsAdmin} />}
                <DividerLabel label={s.settings.dataRetentionPolicy} />
                <div style={{ display: "flex" }}>
                  <FormControlLabel
                    control={<Switch disabled={settingsForced()} checked={settings.reportRetentionDays > 0} onChange={(e, value) => handleChange(e.target.checked, "reportRetentionSwitch")} />}
                    label={s.settings.deleteReportsDays}
                  />
                  <FormControlLabel
                    control={<TextField style={{ width: 100, marginLeft: 8, marginRight: 16 }} type="number" disabled={settingsForced() || (settings.reportRetentionDays == 0)} variant="outlined" size="small" value={settings.reportRetentionDays || ""} onChange={(e, value) => handleChange(Math.max(0, e.target.value), "reportRetentionDays")} />}
                    label={s.common.days.format(settings.reportRetentionDays)}
                  />
                </div>
                <div style={{ display: "flex", marginTop: 16 }}>
                  <FormControlLabel
                    control={<Switch disabled={settingsForced()} checked={settings.libtextRetentionDays > 0} onChange={(e, value) => handleChange(e.target.checked, "libtextRetentionSwitch")} />}
                    label={s.settings.deleteLibtextsDays}
                  />
                  <FormControlLabel
                    control={<TextField style={{ width: 100, marginLeft: 8, marginRight: 16 }} type="number" disabled={settingsForced() || (settings.libtextRetentionDays == 0)} variant="outlined" size="small" value={settings.libtextRetentionDays || ""} onChange={(e, value) => handleChange(Math.max(0, e.target.value), "libtextRetentionDays")} />}
                    label={s.common.days.format(settings.libtextRetentionDays)}
                  />
                </div>
                <DividerLabel label={s.settings.permalinkSharingPolicy} />
                <div style={{ display: "flex" }}>
                  <div style={{ flex: "1 1 auto", width: "50%", marginRight: 8 }}>
                    <TextField disabled={settingsForced()} className={classes.textField} select size="small" label={s.settings.permalinkSharingReadAccess}
                      value={settings.reportPermalinkReadPolicy}
                      onChange={(e) => handleChange(e.target.value, "reportPermalinkReadPolicy")}
                    >
                      <MenuItem key={0} value={0}>{s.settings.permalinkSharingUser}</MenuItem>
                      <MenuItem key={1} value={1}>{s.settings.permalinkSharingLicense}</MenuItem>
                      <MenuItem key={2} value={2}>{s.settings.permalinkSharingPublic}</MenuItem>
                    </TextField>
                  </div>
                  <div style={{ flex: "1 1 auto", width: "50%", marginLeft: 8 }}>
                    <TextField disabled={settingsForced()} className={classes.textField} select size="small" label={s.settings.permalinkSharingWriteAccess}
                      value={settings.reportPermalinkWritePolicy}
                      onChange={(e) => handleChange(e.target.value, "reportPermalinkWritePolicy")}
                    >
                      <MenuItem key={0} value={0}>{s.settings.permalinkSharingUser}</MenuItem>
                      <MenuItem key={1} value={1}>{s.settings.permalinkSharingLicense}</MenuItem>
                      <MenuItem key={2} value={2}>{s.settings.permalinkSharingPublic}</MenuItem>
                    </TextField>
                  </div>
                </div>
              </div>
              {/*}
              <div style={{ flex: "1 1 auto", width: "50%", marginLeft: 8 }}>
                <DividerLabel label="Security Settings" />
                <FormControlLabel className={classes.switch}
                  control={<Switch checked={settings.enablePermaLink} onChange={(e) => handleChange(e.target.checked, "enablePermaLink")} />}
                  label="Enable static link to reports"
                />
                <FormControlLabel className={classes.slider}
                  control={<Slider value={settings.autoLogout} min={0} max={48} valueLabelDisplay="auto" style={{ width: 100, marginRight: 10 }} onChange={(e, value) => handleChange(value, "autoLogout")} />}
                  label={(settings.autoLogout > 0) ? "Auto logout after " + settings.autoLogout + " hours" : "Stay logged in forever"}
                />
              </div>*/}

            </div>
          </TabPanel>

        </DialogContent>
        <DialogActions >
          <Button disabled={!changed} onClick={handleReset} variant="outlined" color="primary">
            {s.common.reset}
          </Button>
          <Button onClick={handleClose} variant="outlined" color="primary">
            {s.common.cancel}
          </Button>
          <Button disabled={!changed} onClick={handleSubmit} variant="contained" color="primary" endIcon={<NavigateNextIcon />} autoFocus>
            {s.common.save}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}